<template>
  <div>
    <!-- Floating Button -->
    <v-btn
      v-if="!chatOpen && !closerobot"
      fab
      dark
      color="primary"
      fixed
      bottom
      right
      class="ma-4 floating-chat-button"
      elevation="8"
      @click="openChat"
    >
      <v-icon large>mdi-robot</v-icon>
    </v-btn>

    <!-- Chat Dialog -->
    <v-dialog
      v-model="chatOpen"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="450"
      hide-overlay
      transition="dialog-bottom-transition"
      scrollable
      persistent
      content-class="chat-dialog-custom"
      class="rounded-xl chat"
    >
      <v-card class="rounded-xl">
        <!-- Chat Header -->
        <v-card-title class="chat-header">
          <v-avatar size="40" class="mr-3">
            <v-icon color="white">mdi-robot</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="text-h6">{{ assistantName || "دستیار هوشمند" }}</div>
            <div class="text-caption text--secondary">
              <v-chip x-small color="success" dark>
                <v-icon x-small left>mdi-circle</v-icon>
                آنلاین
              </v-chip>
            </div>
          </div>
          <v-spacer />
          <v-btn
            class="ml-1 px-0"
            @click="newChat"
            color="#20c997"
            icon
            min-width="30"
            v-b-tooltip.hover
            title="چت جدید"
          >
            <v-icon>mdi-message-plus</v-icon>
          </v-btn>
          <v-btn
            class="ml-1 px-0"
            @click="clearChat"
            color="#f64e60"
            icon
            min-width="30"
            v-b-tooltip.hover
            title="حذف چت"
          >
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
          <v-btn
            @click="closeChatPermanently"
            class="btn-delete rounded-lg ml-1 px-0"
            min-width="30"
            outlined
            depressed
            v-b-tooltip.hover
            title="بستن"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- Chat Messages -->
        <v-card-text
          ref="chatContainer"
          class="chat-messages pa-4"
          style="height: calc(100vh - 200px); overflow-y: auto"
        >
          <div v-if="messages.length === 0" class="text-center py-12">
            <v-icon size="64" color="grey lighten-1">mdi-robot</v-icon>
            <div class="text-h6 mt-4 grey--text">
              {{
                assistantDescription ||
                " سلام! من دستیار هوشمند شما هستم. سوال خود را بپرسید"
              }}
            </div>
          </div>

          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="[
              'message-wrapper mb-4 d-flex',
              message.role !== 'user' ? 'justify-end' : 'justify-start',
            ]"
          >
            <div
              :class="[
                'message-bubble pa-4 rounded-lg',
                message.role === 'user' ? 'user-message' : 'assistant-message',
              ]"
              style="max-width: 70%"
            >
              <div
                v-if="message.role === 'assistant'"
                class="d-flex align-center mb-2"
              >
                <v-avatar size="32" color="primary">
                  <v-icon color="white" small>mdi-robot</v-icon>
                </v-avatar>
                <span class="ml-2 font-weight-bold">دستیار</span>
              </div>
              <div
                class="message-content"
                v-html="
                  formatMessage(
                    index === messages.length - 1 &&
                      message.role === 'assistant' &&
                      isTyping
                      ? typingText
                      : message.content,
                  )
                "
              ></div>
              <!-- نمایش کاراکتر تایپینگ -->
              <span
                v-if="
                  index === messages.length - 1 &&
                  message.role === 'assistant' &&
                  isTyping
                "
                class="typing-cursor"
                >|</span
              >
              <!-- دکمه تمدید/خرید طرح برای خطای 400 -->
              <div v-if="message.showUpgradeButton && !isTyping" class="mt-3">
                <v-btn
                  small
                  outlined
                  class="btn-update rounded-lg"
                  @click="openUpgradeDialog"
                >
                  <v-icon left small>mdi-autorenew</v-icon>
                  تمدید/خرید طرح
                </v-btn>
              </div>

              <div class="message-time text-caption mt-2 text--secondary">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>

          <!-- Loading Indicator -->
          <div v-if="loading" class="d-flex align-center justify-start mb-4">
            <v-progress-circular
              indeterminate
              size="24"
              color="primary"
              class="mr-3"
            />
            <span class="text--secondary">در حال پردازش...</span>
          </div>
        </v-card-text>

        <v-divider />

        <!-- Chat Input -->
        <v-card-actions class="chat-input pa-3">
          <v-btn
            color="primary"
            class="ml-2"
            :disabled="!userMessage.trim() || loading"
            :loading="loading"
            fab
            dark
            @click="sendMessage"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
          <v-textarea
            class="rounded-xl"
            v-model="userMessage"
            placeholder="پیام خود را بنویسید..."
            rows="2"
            auto-grow
            outlined
            hide-details
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact="userMessage += '\n'"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <upgrade-ai
      v-model="showUpgradeDialog"
      :current-package="currentPackage"
      @input="showUpgradeDialog = $event"
      :assistant-id="assistantId"
    />
  </div>
</template>

<script>
import UpgradeAi from "./upgradeAi.vue";
export default {
  name: "AssistantChatTester",
  components: {
    UpgradeAi,
  },
  props: {
    assistantId: {
      type: [Number, String],
      required: true,
    },
    assistantData: {
      type: Object,
      default: null,
    },
    closerobot: { type: Boolean, default: false },
  },

  data() {
    return {
      typingText: "",
      isTyping: false,
      typingTimeout: null,
      displayedText: "",
      index: 0,
      typingSpeed: 50,
      chatOpen: false,
      messages: [],
      userMessage: "",
      loading: false,
      assistantName: "",
      assistantDescription: "",
      promptText: "",
      previse_messages: [],
      showUpgradeButton: false,
      showUpgradeDialog: false,
      currentPackage: null,
    };
  },

  watch: {
    assistantData: {
      handler(newVal) {
        if (newVal) {
          this.loadAssistant(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
  },
  beforeDestroy() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  },
  methods: {
    closeChatPermanently() {
      this.chatOpen = false;
      this.closerobot = true;
      this.$emit("update:closerobot", true);
    },
    typeText(fullText, messageIndex) {
      if (!fullText || fullText.trim() === "") {
        this.isTyping = false;
        return;
      }
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      this.isTyping = true;
      this.typingText = "";
      const totalDuration = fullText.length * this.typingSpeed;
      const charCount = fullText.length;
      const delayPerChar = totalDuration / charCount;
      const adjustedDelay = Math.min(Math.max(delayPerChar, 10), 25);
      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex < charCount) {
          this.typingText = fullText.substring(0, currentIndex + 1);
          currentIndex++;
          this.typingTimeout = setTimeout(typeNextChar, adjustedDelay);

          this.$nextTick(() => {
            this.scrollToBottom();
          });
        } else {
          this.isTyping = false;
          this.typingText = fullText;
          this.typingTimeout = null;
        }
      };
      typeNextChar();
    },
    typeWriter() {
      if (this.index < this.fullText.length) {
        this.displayedText += this.fullText.charAt(this.index);
        this.index++;
        setTimeout(this.typeWriter, this.typingSpeed);
      }
    },
    openChat() {
      this.chatOpen = true;
      this.$nextTick(() => {
        this.scrollToBottom();
      });
      this.$emit("update:closerobot", false);
    },

    async loadAssistant(data) {
      this.assistantName = data.basic_info?.assistant_name || "دستیار هوشمند";
      this.promptText = this.convertJsonToPrompt(data);
    },

    convertJsonToPrompt(jsonData) {
      const basePrompt = `<< این دستیار نمایندهٔ رسمی اطلاعات و راهنمایی‌های مرتبط با محصول/خدمت تعریف‌شده در این پرامپت است.
تمام پاسخ‌ها باید مستقیماً به این محصول یا خدمات وابسته به آن مربوط باشند.
در صورت دریافت سؤال نامرتبط، از پاسخ‌گویی خودداری کرده و با حفظ لحن حرفه‌ای، کاربر را به طرح سؤال مرتبط با محصول هدایت کنید. >>

# مشخصات دستیار

`;
      let prompt = basePrompt;
      return prompt;
    },

    async sendMessage() {
      if (!this.userMessage.trim() || this.loading) return;

      const userMsg = this.userMessage.trim();

      this.userMessage = "";

      this.messages.push({
        role: "user",
        content: userMsg,
        timestamp: new Date(),
      });

      this.loading = true;
      this.scrollToBottom();
      this.showUpgradeButton = false;

      try {
        const { data } = await this.$api.post(this.$aiApi + "/assistant/test", {
          message: userMsg,
          assist_id: this.assistantId,
          previse_messages: this.previse_messages,
        });
        let response =
          data?.data?.output?.[0]?.content?.[0]?.text ||
          data?.output?.[0]?.content?.[0]?.text ||
          data?.text ||
          data?.choices?.[0]?.message?.content ||
          "";
        this.messages.push({
          role: "assistant",
          content: response || "متأسفم، خطایی رخ داد.",
          timestamp: new Date(),
        });
        this.$nextTick(() => {
          this.typeText(
            response || "متأسفم، خطایی رخ داد.",
            this.messages.length - 1,
          );
          this.scrollToBottom();
        });
        this.previse_messages.push(
          {
            role: "user",
            content: userMsg,
          },
          {
            role: "assistant",
            content: response || "متأسفم، خطایی رخ داد.",
          },
        );
      } catch (response) {
        let messageError =
          "متأسفم، در حال حاضر نمی‌توانم پاسخ دهم. لطفاً دوباره تلاش کنید.";
        if (response?.status === 400) {
          messageError = response.data.message;
          this.showUpgradeButton = true;
        }
        console.error("Chat error:", messageError);
        this.$toast.error("خطا در ارتباط با دستیار");
        const errorMessage = {
          role: "assistant",
          content: messageError,
          timestamp: new Date(),
          showUpgradeButton: this.showUpgradeButton,
        };

        this.messages.push(errorMessage);
        this.$nextTick(() => {
          this.typeText(messageError, this.messages.length - 1);
        });
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    openUpgradeDialog() {
      this.currentPackage = {
        id: this.assistantId,
        name: this.assistantName || "دستیار هوشمند",
      };
      this.showUpgradeDialog = true;
    },

    newChat() {
      this.$modal
        .yesNo(
          "آیا می‌خواهید چت جدید شروع کنید؟ تمام پیام‌های قبلی حذف می‌شوند.",
        )
        .then((value) => {
          if (value === true) {
            this.clearChat();
          }
        });
    },

    clearChat() {
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
      this.messages = [];
      this.userMessage = "";
      this.typingText = "";
      this.previse_messages = [];
      this.isTyping = false;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop =
            this.$refs.chatContainer.scrollHeight;
        }
      });
    },

    formatMessage(content) {
      return content.replace(/\n/g, "<br>");
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.closerobotbutton {
  z-index: 1000;
  animation: pulse 2s infinite;
  margin: 0 0 0 0 !important;
  padding: 0px, 0px, 0px, 0px !important;
  bottom: 220px !important;
  padding: 10px !important;
  margin: 0px !important;
  width: 15px !important;
  height: 15px !important;
}
.floating-chat-button {
  z-index: 1000;
  animation: pulse 2s infinite;
  margin: 0 0 0 0 !important;
  padding: 0px, 0px, 0px, 0px !important;
  bottom: 155px;
  width: 60px;
  height: 60px;
}
.floating-chat-button:hover,
.floating-chat-button:active {
  scale: 1.1;
}
.user-message::after {
  content: "";
  position: absolute;
  bottom: 3px;
  right: -12px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 14px solid #764ba2;
  border-right: 0;
}
.assistant-message::after {
  content: "";
  position: absolute;
  bottom: 8px;
  left: -12px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 14px solid #f5f5f5;
  border-left: 0;
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}

.chat-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 24px;
}

.user-message {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  /* border-radius: 18px 18px 4px 18px; */
  border-radius: 18px 18px 8px 18px !important;
  margin-right: 20px;
  position: relative;
}

.assistant-message {
  background: #f5f5f5;
  color: #333;
  /* border-radius: 18px 18px 18px 4px; */
  border-radius: 18px 18px 18px 10px !important;
  margin-left: 18px;
  position: relative;
}

.message-content {
  word-wrap: break-word;
  white-space: pre-wrap;
}

.chat-messages {
  background: #ffffff;
}

.chat-input {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}
.cssdialg {
  max-width: 450px;
  position: fixed;
  bottom: 155px;
  left: 0px;
}
.chat-dialog-custom {
  align-self: flex-end;
  margin: 0 0 155px 0 !important;
  max-width: 450px;
  border-radius: 8px;
}
.typing-text {
  color: #333;
  white-space: pre-wrap;
}
.typing-cursor {
  display: inline-block;
  animation: blink 1s infinite;
  font-weight: bold;
  color: #333;
  margin-right: 2px;
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}
.chat {
  border-radius: 20px !important;
}
.chat-dialog-custom >>> .v-card {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.v-dialog__content {
  align-self: flex-end !important;
  margin-bottom: 155px !important;
}

.chat-dialog-custom {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
  margin: 0 !important;
}

.chat-dialog-custom >>> .v-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.chat-dialog-custom >>> .chat-header {
  border-top-left-radius: 20px !important;
  border-top-right-radius: 20px !important;
}

.chat-dialog-custom >>> .chat-input {
  border-bottom-left-radius: 20px !important;
  border-bottom-right-radius: 20px !important;
}
</style>
